import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'
import { BuildingFile, ListBuildingFiles } from 'types/building'

type Props = {
  currentListFile: BuildingFile[]
  allBuildingFiles?: ListBuildingFiles
  processFiles: (files: File[], option?: number) => void
  allowedExtensions?: string[]
}

export default function useFileDuplicateCheck({
  currentListFile,
  allBuildingFiles,
  processFiles,
  allowedExtensions
}: Props) {
  const { t } = useTranslation()
  const [duplicateFiles, setDuplicateFiles] = useState<
    { newFile: File; oldFile: BuildingFile }[]
  >([])
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [selectedDuplicateOption, setSelectedDuplicateOption] =
    useState<number>(1)
  const pendingFilesRef = useRef<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const validFiles: File[] = []
    const duplicateMatches: { newFile: File; oldFile: BuildingFile }[] = []

    files.forEach((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase() || ''

      const isTypeValid =
        !allowedExtensions ||
        allowedExtensions.length === 0 ||
        allowedExtensions.includes(extension)

      if (!isTypeValid) {
        notification.error({
          message: `${file.name}: ${t(
            'common.validationError.buildingFileNotAllowed'
          )}`
        })
      } else if (file.size / (1024 * 1024) > 50) {
        notification.error({
          message: `${file.name}: ${t('common.validationError.fileTooLarge')}`
        })
      } else {
        const alreadyExistsInAnnotation = (currentListFile || []).find(
          (item) => item.name === file.name
        )

        const alreadyExistsInSystem = allBuildingFiles?.files?.files?.find(
          (item) => item.name === file.name
        )

        const match = alreadyExistsInAnnotation || alreadyExistsInSystem
        if (match) {
          duplicateMatches.push({ newFile: file, oldFile: match })
        } else {
          validFiles.push(file)
        }
      }
    })

    if (duplicateMatches.length > 0) {
      setDuplicateFiles(duplicateMatches)
      pendingFilesRef.current = validFiles
      setShowDuplicateModal(true)
      setSelectedDuplicateOption(1)
    } else {
      processFiles(validFiles)
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleConfirmDuplicate = () => {
    const filesToUpload = [
      ...pendingFilesRef.current,
      ...duplicateFiles.map((d) => d.newFile)
    ]
    processFiles(filesToUpload, selectedDuplicateOption)
    setShowDuplicateModal(false)
    setDuplicateFiles([])
    pendingFilesRef.current = []
  }

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false)
    setDuplicateFiles([])
    pendingFilesRef.current = []
  }

  return {
    duplicateFiles,
    showDuplicateModal,
    selectedDuplicateOption,
    setSelectedDuplicateOption,
    fileInputRef,
    handleFileUpload,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    setDuplicateFiles
  }
}
